/**
 * AgentList — lists seeded + saved agent definitions (`GET /api/agents`).
 *
 * Each agent links to the {@link AgentEditor} canvas; a "New agent" action opens
 * a blank canvas. Data is fetched with TanStack Query through the shared api
 * client (JWT injected), so the list reflects the seeded Trident demo agents
 * plus anything the user has saved.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toolShortLabel, type AgentListResponse } from '../lib/agents';

export function AgentList(): ReactNode {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get<AgentListResponse>('/agents'),
  });

  return (
    <div className="animate-fade-in" data-testid="agent-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-navy-900">Agent Builder</h1>
          <p className="mt-1 text-sm text-steel-600">
            No-code agents that answer over the permission-filtered corpus.
          </p>
        </div>
        <Link to="/agents/new" className="btn-primary">
          New agent
        </Link>
      </div>

      {isLoading && (
        <p className="mt-6 text-sm text-steel-500">Loading agents…</p>
      )}

      {isError && (
        <p className="mt-6 text-sm text-rose-600" data-testid="agent-list-error">
          {error instanceof Error ? error.message : 'Failed to load agents'}
        </p>
      )}

      {data && data.agents.length === 0 && (
        <p className="mt-6 text-sm text-steel-500">
          No agents yet. Create your first agent to get started.
        </p>
      )}

      {data && data.agents.length > 0 && (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.agents.map((agent) => (
            <li key={agent.id}>
              <Link
                to={`/agents/${agent.id}`}
                data-testid="agent-card"
                className="card block hover:shadow-md transition-shadow h-full"
              >
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-base font-semibold text-navy-900">
                    {agent.name}
                  </h2>
                  <span className="text-[11px] text-steel-400 shrink-0">
                    v{agent.version}
                  </span>
                </div>
                {agent.description && (
                  <p className="mt-2 text-sm text-steel-600 line-clamp-2">
                    {agent.description}
                  </p>
                )}
                {agent.node_types && agent.node_types.length > 0 && (
                  <div
                    className="mt-3 flex flex-wrap gap-1"
                    aria-label="Tools used by this agent"
                  >
                    {agent.node_types.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-steel-100 px-2 py-0.5 text-[11px] text-steel-700"
                      >
                        {toolShortLabel(t)}
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-3 text-[11px] text-steel-400">
                  {agent.node_count} node{agent.node_count === 1 ? '' : 's'} ·
                  updated {formatDate(agent.updated_at)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Format an ISO timestamp as a short, locale-aware date (falls back to raw). */
function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default AgentList;
